import { type JSX, useCallback, useEffect, useState } from 'react';
import Typography from 'carbon-react/esm/components/typography';
import Box from 'carbon-react/esm/components/box';
import { Select, Option } from 'carbon-react/esm/components/select';
import Button from 'carbon-react/esm/components/button/__next__';
import ProfilesOverview from './ProfilesOverview';
import { useProfilesContext } from '../../../../context/useProfilesContext';
import { useResponsive } from '../../../../hooks/useResponsive';
import { useNotificationWithAutoClose } from '../../../../utils/notificationHelpers';
import { NotificationMessage } from '../../../../components/NotificationMessage';

const ProfilesTab = (): JSX.Element => {
  const { isMobile } = useResponsive();
  const { profiles, loading, error, updateDefaultProfile } = useProfilesContext();
  const [defaultProfileId, setDefaultProfileId] = useState<string>('');
  const [isDefaultProfileDisabled, setIsDefaultProfileDisabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const {
    isVisibleNotification,
    notificationMessage,
    notificationVariant,
    displayNotification,
    hideNotification,
  } = useNotificationWithAutoClose();

  useEffect(() => {
    if (!defaultProfileId && profiles.length > 0) {
      const defaultProfile = profiles.find(profile => profile.default) ?? profiles[0];
      setDefaultProfileId(defaultProfile.id);
    }
  }, [defaultProfileId, profiles]);

  const handleSaveDefaultProfile = useCallback(async () => {
    setIsSaving(true);
    try {
      const { success, error: errorMessage } = await updateDefaultProfile(defaultProfileId);
      if (success) {
        setIsDefaultProfileDisabled(true);
      } else {
        displayNotification(
          errorMessage ?? 'Erreur lors de la mise à jour du profil par défaut',
          'error',
        );
      }
    } finally {
      setIsSaving(false);
    }
  }, [defaultProfileId, updateDefaultProfile, displayNotification]);

  const handleCancel = useCallback(() => {
    setTimeout(() => {
      setIsDefaultProfileDisabled(true);
      setDefaultProfileId('');
    }, 0);
  }, []);

  const handleModify = useCallback(() => {
    setTimeout(() => {
      setIsDefaultProfileDisabled(false);
    }, 0);
  }, []);

  const handleProfileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | { target: { value: string } }) => {
      setDefaultProfileId(event.target.value);
    },
    [],
  );

  return (
    <Box bg="white" p="23px" width="100%" minWidth={0} boxSizing="border-box">
      <Typography variant="h2" mb={16}>
        Mes droits utilisateurs
      </Typography>
      <Box
        borderRadius="borderRadius000"
        display={'flex'}
        width="100%"
        flexDirection="column"
        gap="var(--spacing100)"
      >
        <Box
          display="flex"
          alignItems={isMobile ? 'flex-start' : 'flex-end'}
          maxWidth={500}
          flexDirection={isMobile ? 'column' : 'row'}
          gap={2}
          mb={4}
        >
          <Box width={300}>
            <Typography variant="b" mb={1}>
              Profil par défaut
            </Typography>
            <Select
              disabled={isDefaultProfileDisabled || loading || profiles.length === 0 || isSaving}
              name="profils"
              size="small"
              aria-label="Profil par défaut"
              value={defaultProfileId}
              onChange={handleProfileChange}
            >
              {profiles.map(profile => (
                <Option key={profile.id} text={profile.name} value={profile.id} />
              ))}
            </Select>
          </Box>

          {!isDefaultProfileDisabled ? (
            <Box display="flex" flexShrink={0} gap={2}>
              <Button
                variantType="subtle"
                size="small"
                fullWidth={isMobile}
                onClick={handleCancel}
                disabled={isSaving}
              >
                Annuler
              </Button>
              <Button
                variantType="primary"
                size="small"
                fullWidth={isMobile}
                onClick={handleSaveDefaultProfile}
                disabled={isSaving}
              >
                {isSaving ? 'Enregistrement...' : 'Valider'}
              </Button>
            </Box>
          ) : (
            <Button variantType="primary" size="small" fullWidth={isMobile} onClick={handleModify}>
              Modifier
            </Button>
          )}
        </Box>
      </Box>

      {error ? (
        <Box color="red">Erreur lors du chargement des profils: {error}</Box>
      ) : (
        <ProfilesOverview />
      )}

      <NotificationMessage
        open={isVisibleNotification}
        onDismiss={hideNotification}
        variant={notificationVariant}
      >
        {notificationMessage}
      </NotificationMessage>
    </Box>
  );
};

export default ProfilesTab;
